import { useSyncExternalStore } from 'react';
import {
  createIdbStore, getOutbox, initOutbox, kindRegistry, ITEM_TTL_MS, OutboxSendError, registerKind, type KindHandler, type Outbox, type OutboxItem, type OutboxStore,
} from '@/lib/outbox';
import type { FeedbackPayload } from './api';

/** The outbox kind of a note written without a connection. One step, one POST; the note's own id is the idempotency key. */
export const FEEDBACK_KIND = 'feedback.send';

const handler: KindHandler<FeedbackPayload> = {
  kind: FEEDBACK_KIND,
  paths: ['/v1/feedback'],
  // outside /crew the 24 h keys expire before a 7 day item does: the server dedupes on the note id instead (same id twice, one note)
  naturalIdempotency: true,
  firstStep: 'send',
  async step(item, ctx) {
    // event: false, so the body is exactly the payload (it carries client_time itself, and no client_event_id the contract lacks)
    try {
      await ctx.send({ method: 'POST', path: '/v1/feedback', step: 'send', event: false, body: item.payload });
    } catch (e) {
      // 404: the feedback flag is off for this business now. Nobody will ever see this note, so it is deleted, not kept as a hidden failure.
      if (e instanceof OutboxSendError && e.res.status === 404) return { done: true };
      throw e;
    }
    return { done: true };
  },
  refetch: () => [],
};

/** Registers the kind once. Imported at startup (App and the native bootstrap), never from the lazy sheet, so a note queued before a relaunch finds its handler. */
export function registerFeedbackKind(): void {
  if (!kindRegistry.get(FEEDBACK_KIND)) registerKind(handler);
}
registerFeedbackKind();

/**
 * Deletes queued notes (their text is on the device): those of `userId`, and/or those older than `olderThanMs` whoever owns them. A note
 * that waited past the outbox's 7 day life is only ever shown to its own user, who may never come back; this closes that.
 */
export async function purgeFeedbackItems(store: Pick<OutboxStore, 'list' | 'remove'>, by: { userId?: number; olderThanMs?: number }): Promise<void> {
  const now = Date.now();
  for (const item of await store.list()) {
    if (item.kind !== FEEDBACK_KIND) continue;
    const mine = by.userId !== undefined && item.userId === by.userId;
    const old = by.olderThanMs !== undefined && now - Date.parse(item.createdAt) > by.olderThanMs;
    if (mine || old) await store.remove(item.id);
  }
}
/** Notes older than the outbox's life (7 days) are deleted at startup, for every user. */
export const sweepStaleFeedback = (store: Pick<OutboxStore, 'list' | 'remove'>) => purgeFeedbackItems(store, { olderThanMs: ITEM_TTL_MS });

let webStorePromise: Promise<OutboxStore> | null = null;
/** The website's IndexedDB store, opened once. */
function webStore(): Promise<OutboxStore> {
  webStorePromise ??= createIdbStore();
  return webStorePromise;
}
/** Website sign-out: the person's queued notes are removed from this browser (a shared computer must not keep their words). */
export async function purgeWebFeedbackFor(userId: number): Promise<void> {
  try {
    await purgeFeedbackItems(await webStore(), { userId });
  } catch {
    // no IndexedDB here: nothing was kept
  }
}

let web: Promise<Outbox> | null = null;
/** The website's outbox (IndexedDB), made once. The app's is made by the native bootstrap, and returned as is. */
export function ensureOutbox(): Promise<Outbox> {
  const existing = getOutbox();
  if (existing) return Promise.resolve(existing);
  web ??= webStore().then(async (store) => {
    await sweepStaleFeedback(store).catch(() => undefined);
    return initOutbox({ store });
  });
  return web;
}

/** Queues a note for the signed-in user (the outbox must have been given its identity: FeedbackRuntime does that). */
export async function queueFeedback(payload: FeedbackPayload): Promise<void> {
  const outbox = getOutbox();
  if (!outbox) throw new Error('The outbox is not running');
  await outbox.enqueue({ kind: FEEDBACK_KIND, payload, clientTime: payload.client_time });
}

// ---- the queue as the screens see it ------------------------------------------------------------------------------------

let queue: OutboxItem[] = [];
const listeners = new Set<() => void>();
export function setFeedbackQueue(items: OutboxItem[]): void {
  queue = items.filter((i) => i.kind === FEEDBACK_KIND);
  listeners.forEach((l) => l());
}
/** The signed-in user's queued notes (waiting and not sent), oldest first. */
export function useFeedbackQueue(): OutboxItem[] {
  return useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, () => queue);
}

/**
 * Watches the outbox: publishes the queued notes, and calls `onSent` when one that was waiting is gone without having failed
 * (the engine removes an item the server took). Starts from the current list, so a note queued before a relaunch is counted.
 */
export function watchFeedbackQueue(outbox: Pick<Outbox, 'list' | 'subscribe'>, onSent: () => void): () => void {
  let waiting = new Set<string>();
  let live = true;
  const apply = (items: OutboxItem[], announce: boolean) => {
    const mine = items.filter((i) => i.kind === FEEDBACK_KIND);
    const now = new Set(mine.filter((i) => i.state !== 'failed' && i.state !== 'done').map((i) => i.id));
    if (announce && [...waiting].some((id) => !now.has(id) && !mine.some((i) => i.id === id))) onSent();
    waiting = now;
    setFeedbackQueue(items);
  };
  void outbox.list().then((items) => { if (live) apply(items, false); }).catch(() => undefined);
  const stop = outbox.subscribe((items) => { if (live) apply(items, true); });
  return () => {
    live = false;
    stop();
    setFeedbackQueue([]);
  };
}
